import React, { useState, useMemo } from 'react';

import { queryClient } from '@vlabs/api';
import {
  getGetCardQueryOptions, useGetCard, useUpdateLinker, useGetLists,
} from '@vlabs/api/artemy/client';
import type { CardId } from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { Control, Dropdown, DropdownItem } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './AttachCardToListButton.module.sass';

interface AttachCardToListButtonProps {
  cardId: CardId;
  disabled?: boolean;
  onAttachCardToList?: VoidFunction;
}

export function AttachCardToListButton({
  cardId,
  disabled = false,
  onAttachCardToList = undefined,
}: AttachCardToListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const getCard = useGetCard(cardId, { targets: ['lists'] });
  const { data: listsData } = useGetLists({ page_size: 1000 });
  const listOptions = useMemo(
    () => listsData?.lists?.map((l) => ({ value: l.list_id, label: l.user_data })) ?? [],
    [listsData],
  );
  const linkCards = useUpdateLinker({
    mutation: {
      onSuccess: () => {
        // FIXME: orval генерирует кривой инвалидатор
        queryClient.invalidateQueries(getGetCardQueryOptions(cardId));
        onAttachCardToList?.();
      },
    },
  });

  // FIXME empty-заглушка при отсутствии опций предусмотрена доработкой uikit в GUI-3410
  const dropdownContent = useMemo(() => {
    if (!Array.isArray(listOptions)) return [];
    const $attachedListIds = new Set(getCard.data?.lists ?? []);
    const unattachedLists = listOptions.filter((listOption) => !$attachedListIds.has(listOption.value));

    return unattachedLists.map(({ value: listId, label }) => {
      return (
        <DropdownItem
          className={st.ListOption}
          data-testid={`PersonLists.List_${listId}`}
          key={listId}
          label={label}
          onClick={async () => {
            await linkCards.mutateAsync({
              data: {
                action: 'attach',
                card_ids: [cardId],
                list_id: listId,
              },
            });
            setIsOpen(false);
          }}
        />
      );
    });
  }, [getCard.data, listOptions, cardId, linkCards]);

  return (
    <Dropdown
      data-testid="PersonLists.Lists"
      isOpen={isOpen}
      menuClassName={st.List}
      onClose={() => setIsOpen(false)}
      options={dropdownContent}
      placement="bottom-right"
      searchable
    >
      <Control.IconButton
        data-testid="PersonLists.AttachListButton"
        disabled={disabled}
        icon={<PlusIcon />}
        kind="secondary"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        title={t('card-page:buttons.attach card to list.tooltip') as unknown as string}
      />
    </Dropdown>
  );
}
